"use client";

// Register v2 — centered card sobre crema. Recibe ?plan=starter|growth desde
// la landing. Tras crear la cuenta, screen de éxito le dice qué hacer.
//
// Lógica preservada del v1:
//   - registerUser server action crea auth.user + company + public.users
//   - checkSlugAvailable con debounce 350ms para live availability
//   - Password rules con checklist visual (min 8, mayús, minús, num/símbolo)
//   - Confirmar password (debe coincidir)
//   - Aceptar Términos + Privacidad obligatorio
//   - Slug auto-derivado del companyName (slugify) hasta que el user lo toque
//   - Suspense wrapper para useSearchParams (Next 14 prerender-safe)

import { Suspense, useEffect, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Check, X, Loader2 } from "lucide-react";
import { registerUser, checkSlugAvailable } from "./actions";
import { BrandMark } from "@/components/brand/BrandMark";
import { PublicShell } from "@/components/brand/PublicShell";
import { PWD_RULES, PWD_RULE_LABELS, passwordStrong } from "@/lib/security/password-rules";

type Plan = "lite" | "starter" | "growth";
const VALID_PLANS: Plan[] = ["lite", "starter", "growth"];

const PLAN_INFO: Record<Plan, { name: string; tag: string }> = {
  lite:    { name: "Lite",    tag: "Plan mensual" },
  starter: { name: "Starter", tag: "Plan mensual" },
  growth:  { name: "Growth",  tag: "Plan mensual" },
};

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

export default function RegisterPage({ params }: { params: { locale: string } }) {
  return (
    <Suspense fallback={null}>
      <RegisterForm params={params} />
    </Suspense>
  );
}

type PromoState =
  | { status: "idle" }
  | { status: "validating" }
  | { status: "applied"; code: string; descriptionShort: string; originalUsd: number; discountedUsd: number }
  | { status: "error"; message: string };

function RegisterForm({ params }: { params: { locale: string } }) {
  const searchParams = useSearchParams();
  const planParam = (searchParams.get("plan") ?? "starter") as Plan;
  const plan: Plan = VALID_PLANS.includes(planParam) ? planParam : "starter";
  const planInfo = PLAN_INFO[plan];

  // Si vino ?promo=XXX en la URL (campaña LinkedIn / mail), lo pre-llenamos
  // y disparamos auto-validación.
  const promoFromUrl = (searchParams.get("promo") ?? "").trim().toUpperCase();

  const [form, setForm] = useState({
    email: "", password: "", passwordConfirm: "",
    fullName: "", companyName: "", slug: "",
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState<
    "idle" | "checking" | "available" | "taken" | "reserved" | "invalid"
  >("idle");
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [pwdTouched, setPwdTouched] = useState(false);
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Estado del código promocional — colapsado por default. Si la URL trae
  // ?promo=XXX lo abrimos automático.
  const [promoOpen, setPromoOpen] = useState(!!promoFromUrl);
  const [promoInput, setPromoInput] = useState(promoFromUrl);
  const [promoState, setPromoState] = useState<PromoState>({ status: "idle" });

  const pwdOK = passwordStrong(form.password);
  const pwdMatches = form.password.length > 0 && form.password === form.passwordConfirm;

  // Auto-slug desde el companyName hasta que el user lo toque manualmente.
  useEffect(() => {
    if (slugTouched) return;
    setForm((p) => ({ ...p, slug: slugify(p.companyName) }));
  }, [form.companyName, slugTouched]);

  // Live availability con debounce 350ms y cancelación de race conditions.
  useEffect(() => {
    const s = form.slug.trim();
    if (!s) { setSlugStatus("idle"); return; }
    setSlugStatus("checking");
    let cancelled = false;
    const t = setTimeout(async () => {
      const r = await checkSlugAvailable(s);
      if (cancelled) return;
      setSlugStatus(r.status === "empty" ? "idle" : r.status);
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.slug]);

  function update<K extends keyof typeof form>(key: K) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((p) => ({ ...p, [key]: e.target.value }));
  }

  // Validación del código promocional contra el endpoint server. El server
  // hace el lookup del precio del plan (NUNCA confiamos en client-side para
  // el precio) y nos devuelve el descuento calculado para mostrarlo en UI.
  async function validatePromo(rawCode: string) {
    const code = rawCode.trim().toUpperCase();
    if (!code) {
      setPromoState({ status: "idle" });
      return;
    }
    setPromoState({ status: "validating" });
    try {
      const res = await fetch("/api/promo/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, plan }),
      });
      const data = await res.json();
      if (data?.ok) {
        setPromoState({
          status: "applied",
          code: data.code,
          descriptionShort: data.description_short,
          originalUsd: data.original_price_usd,
          discountedUsd: data.discounted_price_usd,
        });
      } else {
        setPromoState({
          status: "error",
          message: data?.message ?? "Código inválido",
        });
      }
    } catch (e) {
      setPromoState({ status: "error", message: "Error al validar. Probá de nuevo." });
    }
  }

  // Auto-validar el código si vino por URL al cargar la página.
  useEffect(() => {
    if (promoFromUrl) {
      validatePromo(promoFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!pwdOK) { setPwdTouched(true); setError("La contraseña no cumple los requisitos."); return; }
    if (!pwdMatches) { setPwdTouched(true); setError("Las contraseñas no coinciden."); return; }
    if (!accept) { setError("Tenés que aceptar los Términos y la Política de privacidad."); return; }
    if (slugStatus === "taken" || slugStatus === "reserved" || slugStatus === "invalid") {
      setError(
        slugStatus === "taken" ? "Ese identificador de portal ya está en uso, elegí otro."
        : slugStatus === "reserved" ? "Ese identificador está reservado, elegí otro."
        : "Identificador inválido. Usá 3-40 caracteres: letras, números y guiones."
      );
      return;
    }
    setLoading(true);
    const r = await registerUser({
      email: form.email.trim().toLowerCase(),
      password: form.password,
      fullName: form.fullName.trim(),
      companyName: form.companyName.trim(),
      slug: form.slug.trim().toLowerCase(),
      plan,
      // Solo mandamos el código si el cliente lo validó OK. Si está en error/idle
      // no lo mandamos — evitamos que un código mal escrito haga ruido en logs.
      promoCode: promoState.status === "applied" ? promoState.code : undefined,
    });
    setLoading(false);
    if (r.error) { setError(r.error); return; }
    setDone(true);
  }

  if (done) return <SuccessScreen plan={plan} email={form.email} locale={params.locale} />;

  return (
    <PublicShell locale={params.locale} maxWidth={460}>
      <div className="card-header">
        <div className="card-icon">
          <BrandMark size={44} rounded={11} />
          talenv
        </div>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          Plan {planInfo.name} · {planInfo.tag}
        </div>
        <h1 className="card-title">Creá tu cuenta</h1>
        <p className="card-sub">Completá estos datos, confirmá tu email y entrás al producto.</p>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="field">
          <label className="field-label" htmlFor="fullName">Nombre y apellido</label>
          <input
            id="fullName"
            type="text"
            className="input"
            placeholder="Juan Pérez"
            autoComplete="name"
            required
            value={form.fullName}
            onChange={update("fullName")}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="email">Email de trabajo</label>
          <input
            id="email"
            type="email"
            className="input"
            placeholder="vos@empresa.com"
            autoComplete="email"
            required
            value={form.email}
            onChange={update("email")}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="password">Contraseña</label>
          <div className="input-wrap">
            <input
              id="password"
              type={showPwd ? "text" : "password"}
              className="input with-toggle"
              placeholder="Mínimo 8 caracteres"
              autoComplete="new-password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => { setPwdTouched(true); update("password")(e); }}
            />
            <button
              type="button"
              className="pw-toggle"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
            >
              <EyeIcon visible={showPwd} />
            </button>
          </div>
          {(pwdTouched || form.password.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginTop: 8 }}>
              <PwdRule ok={PWD_RULES.min(form.password)} label={PWD_RULE_LABELS.min} />
              <PwdRule ok={PWD_RULES.upper(form.password)} label={PWD_RULE_LABELS.upper} />
              <PwdRule ok={PWD_RULES.lower(form.password)} label={PWD_RULE_LABELS.lower} />
              <PwdRule ok={PWD_RULES.numOrSym(form.password)} label={PWD_RULE_LABELS.numOrSym} />
            </div>
          )}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="pwd2">Repetí la contraseña</label>
          <div className="input-wrap">
            <input
              id="pwd2"
              type={showPwd2 ? "text" : "password"}
              className="input with-toggle"
              placeholder="••••••••"
              autoComplete="new-password"
              required
              minLength={8}
              value={form.passwordConfirm}
              onChange={update("passwordConfirm")}
            />
            <button
              type="button"
              className="pw-toggle"
              onClick={() => setShowPwd2((v) => !v)}
              aria-label={showPwd2 ? "Ocultar contraseña" : "Mostrar contraseña"}
            >
              <EyeIcon visible={showPwd2} />
            </button>
          </div>
          {form.passwordConfirm.length > 0 && (
            <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5 }}>
              {pwdMatches ? (
                <>
                  <Check size={12} strokeWidth={2.5} color="#1F9D6F" />
                  <span style={{ color: "#1F9D6F" }}>Coinciden</span>
                </>
              ) : (
                <>
                  <X size={12} strokeWidth={2.5} color="#DC2626" />
                  <span style={{ color: "#DC2626" }}>No coinciden</span>
                </>
              )}
            </div>
          )}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="companyName">Nombre de la empresa</label>
          <input
            id="companyName"
            type="text"
            className="input"
            placeholder="empresa"
            autoComplete="organization"
            required
            value={form.companyName}
            onChange={update("companyName")}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="slug">Identificador del portal de empleo</label>
          <div className="input-wrap">
            <input
              id="slug"
              type="text"
              className="input with-toggle"
              placeholder="empresa"
              pattern="[a-z0-9][a-z0-9-]{1,38}[a-z0-9]"
              required
              value={form.slug}
              onChange={(e) => { setSlugTouched(true); setForm((p) => ({ ...p, slug: e.target.value.toLowerCase() })); }}
            />
            <span
              style={{
                position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                display: "inline-flex", alignItems: "center", lineHeight: 0,
              }}
            >
              {slugStatus === "checking" && <Loader2 size={14} strokeWidth={2} color="#6B6982" className="animate-spin" />}
              {slugStatus === "available" && <Check size={15} strokeWidth={2.6} color="#1F9D6F" />}
              {(slugStatus === "taken" || slugStatus === "reserved" || slugStatus === "invalid") &&
                <X size={15} strokeWidth={2.6} color="#DC2626" />}
            </span>
          </div>
          <div style={{ marginTop: 6, fontSize: 11.5, color: slugStatus === "available" ? "#1F9D6F" : slugStatus === "taken" || slugStatus === "reserved" || slugStatus === "invalid" ? "#DC2626" : "#A19FB6", lineHeight: 1.4 }}>
            {slugStatus === "available" && "Disponible"}
            {slugStatus === "taken" && "Ese identificador ya está en uso"}
            {slugStatus === "reserved" && "Ese identificador está reservado"}
            {slugStatus === "invalid" && "Usá 3-40 caracteres: letras, números y guiones"}
            {(slugStatus === "idle" || slugStatus === "checking") && (form.slug
              ? <>Tu portal: <span style={{ fontFamily: "ui-monospace, monospace", color: "#15103D" }}>talenv.com/portal/{form.slug}</span></>
              : "Letras, números y guiones. 3-40 caracteres.")}
          </div>
        </div>

        {/* Campo opcional de código promocional. Colapsable por default —
            si el cliente viene con ?promo=XXX en la URL se abre y valida
            automáticamente. */}
        <div className="field" style={{ marginTop: 4 }}>
          {!promoOpen ? (
            <button
              type="button"
              onClick={() => setPromoOpen(true)}
              style={{
                background: "transparent", border: "none", padding: 0,
                color: "#6C5CFF", fontSize: 13, fontWeight: 500, cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              ¿Tenés un código promocional?
            </button>
          ) : (
            <>
              <label className="field-label" htmlFor="promoCode">Código promocional</label>
              <div className="input-wrap" style={{ display: "flex", gap: 8 }}>
                <input
                  id="promoCode"
                  type="text"
                  className="input"
                  placeholder=""
                  autoComplete="off"
                  maxLength={20}
                  value={promoInput}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setPromoInput(v);
                    // Si edita después de aplicar, reseteamos el estado.
                    if (promoState.status !== "idle") setPromoState({ status: "idle" });
                  }}
                  style={{ flex: 1, textTransform: "uppercase" }}
                />
                {promoState.status === "applied" ? (
                  <button
                    type="button"
                    onClick={() => {
                      setPromoInput("");
                      setPromoState({ status: "idle" });
                    }}
                    style={{
                      padding: "0 14px", borderRadius: 10, border: "1px solid rgba(21,16,61,0.12)",
                      background: "white", color: "#15103D", fontSize: 13, fontWeight: 500, cursor: "pointer",
                    }}
                  >
                    Quitar
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => validatePromo(promoInput)}
                    disabled={!promoInput.trim() || promoState.status === "validating"}
                    style={{
                      padding: "0 14px", borderRadius: 10, border: "none",
                      background: "#6C5CFF", color: "white", fontSize: 13, fontWeight: 600,
                      cursor: promoInput.trim() ? "pointer" : "not-allowed",
                      opacity: promoInput.trim() ? 1 : 0.5,
                    }}
                  >
                    {promoState.status === "validating" ? "..." : "Aplicar"}
                  </button>
                )}
              </div>
              {promoState.status === "applied" && (
                <div style={{
                  marginTop: 8, padding: "10px 12px", borderRadius: 10,
                  background: "#EDFCF4", border: "1px solid #B7F0CE",
                  fontSize: 12.5, color: "#106B3C", lineHeight: 1.5,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>
                    ✓ {promoState.descriptionShort}
                  </div>
                  <div style={{ color: "#1F9D6F" }}>
                    Antes: <span style={{ textDecoration: "line-through" }}>USD {promoState.originalUsd}/mes</span>
                    {" · "}
                    Ahora: <strong>USD {promoState.discountedUsd}/mes</strong>
                  </div>
                </div>
              )}
              {promoState.status === "error" && (
                <div style={{
                  marginTop: 8, padding: "10px 12px", borderRadius: 10,
                  background: "#FEF2F2", border: "1px solid #FECACA",
                  fontSize: 12.5, color: "#991B1B",
                }}>
                  ⚠ {promoState.message}
                </div>
              )}
            </>
          )}
        </div>

        <label
          style={{
            display: "flex", alignItems: "flex-start", gap: 10,
            marginTop: 8, padding: "10px 12px", borderRadius: 10,
            background: accept ? "#ECE8FF" : "#FAF7F2",
            border: `1px solid ${accept ? "#6C5CFF" : "rgba(21,16,61,0.08)"}`,
            cursor: "pointer", transition: "border-color .15s ease, background .15s ease",
          }}
        >
          <input
            type="checkbox"
            checked={accept}
            onChange={(e) => setAccept(e.target.checked)}
            required
            style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, accentColor: "#6C5CFF", cursor: "pointer" }}
          />
          <span style={{ fontSize: 12.5, color: "#6B6982", lineHeight: 1.55 }}>
            Acepto los{" "}
            <Link href={`/${params.locale}/terms`} target="_blank" style={{ color: "#6C5CFF", textDecoration: "underline" }}>
              Términos y Condiciones
            </Link>
            {" "}y la{" "}
            <Link href={`/${params.locale}/privacy`} target="_blank" style={{ color: "#6C5CFF", textDecoration: "underline" }}>
              Política de privacidad
            </Link>.
          </span>
        </label>

        {error && (
          <div className="error-box">
            {error}
            {/Ya existe.*cuenta.*email/i.test(error) && (
              <span style={{ display: "block", marginTop: 6 }}>
                Probá <Link href={`/${params.locale}/login`} style={{ color: "#6C5CFF", textDecoration: "underline" }}>iniciar sesión</Link>
                {" "}o <Link href={`/${params.locale}/forgot-password`} style={{ color: "#6C5CFF", textDecoration: "underline" }}>recuperar tu contraseña</Link>.
              </span>
            )}
          </div>
        )}

        <button type="submit" className="btn-submit" disabled={loading || !accept}>
          {loading ? "Creando cuenta…" : "Crear cuenta y continuar"}
        </button>
      </form>

      <div className="card-foot">
        ¿Ya tenés cuenta? <Link href={`/${params.locale}/login`}>Iniciá sesión</Link>
      </div>
    </PublicShell>
  );
}

function SuccessScreen({ plan, email, locale }: { plan: Plan; email: string; locale: string }) {
  const planInfo = PLAN_INFO[plan];
  return (
    <PublicShell locale={locale}>
      <div style={{ textAlign: "center" }}>
        <div style={{
          width: 56, height: 56, borderRadius: 14,
          background: "#6C5CFF", color: "white",
          display: "grid", placeItems: "center",
          margin: "0 auto 18px",
          boxShadow: "0 8px 22px -8px rgba(108,92,255,0.6), inset 0 1px 0 rgba(255,255,255,0.3)",
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 6l-10 7L2 6" />
            <rect x="2" y="4" width="20" height="16" rx="2" />
          </svg>
        </div>
        <h1 className="card-title">Revisá tu email</h1>
        <p className="card-sub" style={{ marginTop: 8 }}>
          Mandamos un link de confirmación a <strong style={{ color: "#15103D" }}>{email}</strong>.
          Clickealo desde la misma computadora para activar tu cuenta.
        </p>
        <div style={{
          marginTop: 18, padding: 14, borderRadius: 10,
          background: "#ECE8FF",
          border: "1px solid rgba(108,92,255,0.32)",
          textAlign: "left",
        }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "#4E3FE8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
            Plan {planInfo.name}
          </div>
          <div style={{ fontSize: 13, color: "#15103D", lineHeight: 1.55 }}>
            Apenas confirmes tu email e ingreses, te llevamos al checkout para activar tu plan {planInfo.name}.
          </div>
        </div>
        <Link
          href={`/${locale}/login`}
          className="btn-submit"
          style={{ marginTop: 20, textDecoration: "none" }}
        >
          Ir al login
        </Link>
        <p style={{ marginTop: 14, fontSize: 11.5, color: "#A19FB6", lineHeight: 1.55 }}>
          ¿No te llegó? Revisá spam o escribinos a{" "}
          <a href="mailto:hola@talenv.com" style={{ color: "#6C5CFF", textDecoration: "none" }}>hola@talenv.com</a>.
        </p>
      </div>
    </PublicShell>
  );
}

function EyeIcon({ visible }: { visible: boolean }) {
  return visible ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.79 19.79 0 014.22-5.94M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a19.5 19.5 0 01-2.16 3.19M14.12 14.12a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function PwdRule({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      fontSize: 11.5, color: ok ? "#1F9D6F" : "#A19FB6",
      transition: "color .15s ease",
    }}>
      {ok
        ? <Check size={12} strokeWidth={2.6} />
        : <X size={12} strokeWidth={2.4} style={{ opacity: 0.7 }} />}
      {label}
    </span>
  );
}
